/**
 * Image Optimization Utilities
 * Helper functions for optimizing image loading and performance
 */

/**
 * Generate optimized Unsplash URL with specific parameters
 * @param {string} imageUrl - Original Unsplash URL
 * @param {object} options - Optimization options
 * @returns {string} Optimized image URL
 */
export const optimizeUnsplashImage = (imageUrl, options = {}) => {
  const {
    width = 800,
    height = null,
    quality = 80,
    format = 'auto',
    fit = 'crop'
  } = options;

  if (!imageUrl || !imageUrl.includes('unsplash.com')) {
    return imageUrl;
  }

  const url = new URL(imageUrl);
  const params = new URLSearchParams(url.search);

  // Set optimization parameters
  params.set('auto', format);
  params.set('fit', fit);
  params.set('q', quality);
  params.set('w', width);
  
  if (height) {
    params.set('h', height);
  }

  url.search = params.toString();
  return url.toString();
};

/**
 * Generate responsive image srcset
 * @param {string} imageUrl - Base image URL
 * @param {array} widths - Array of widths for srcset
 * @returns {string} srcset string
 */
export const generateSrcSet = (imageUrl, widths = [400, 800, 1200, 1600]) => {
  if (!imageUrl) return '';

  return widths
    .map(width => {
      const optimizedUrl = optimizeUnsplashImage(imageUrl, { width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Get appropriate sizes attribute for responsive images
 * @param {string} type - Image type (hero, card, thumbnail)
 * @returns {string} sizes attribute
 */
export const getImageSizes = (type = 'default') => {
  const sizeMap = {
    hero: '100vw',
    card: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw',
    thumbnail: '(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw',
    featured: '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px',
    default: '100vw'
  };

  return sizeMap[type] || sizeMap.default;
};

/**
 * Preload critical images
 * @param {array} imageUrls - Array of image URLs to preload
 */
export const preloadImages = (imageUrls) => {
  if (!Array.isArray(imageUrls)) return;

  imageUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = optimizeUnsplashImage(url, { width: 800, quality: 80 });
    document.head.appendChild(link);
  });
};

/**
 * Lazy load images using Intersection Observer
 * @param {HTMLElement} element - Image element to observe
 * @param {function} callback - Callback when image is visible
 */
export const lazyLoadImage = (element, callback) => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          callback();
          observer.unobserve(entry.target);
        }
      });
    },
    {
      rootMargin: '50px' // Start loading 50px before entering viewport
    }
  );

  observer.observe(element);
};